import React, { useEffect, useState, useMemo, useCallback } from "react";
import "../detailNewspage/styles/index.less";
import {
  Col,
  Row,
  Typography,
  Layout,
  Card,
  Image,
  message,
  Divider,
  Spin,
  Breadcrumb,
  Empty,
} from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { get_news, get_suggest_news } from "../../services/news";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/th";
import buddhistEra from "dayjs/plugin/buddhistEra";
import DOMPurify from "dompurify";

dayjs.extend(buddhistEra);

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

// ✅ Type Definitions
interface NewsDetail {
  id: number;
  text: string;
}

interface NewsData {
  id: string;
  created_date: string;
  img: string;
  details: NewsDetail[] | string;
  title: string;
}

interface SuggestNews {
  id: string;
  title: string;
  news_id: string;
  created_date: string;
  img: string;
}

// ✅ Custom Hook สำหรับ Responsive
const useResponsive = () => {
  const [dimensions, setDimensions] = useState({
    maxLength: 20,
    fontSize: "32px",
  });

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      
      let maxLength = 20;
      if (width > 1200) maxLength = 50;
      else if (width > 768) maxLength = 30;
      
      let fontSize = "32px";
      if (width < 576) fontSize = "20px";
      else if (width < 768) fontSize = "24px";
      else if (width < 992) fontSize = "28px";
      
      setDimensions({ maxLength, fontSize });
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return dimensions;
};

// ✅ Utility Functions
const getImageUrl = (img: string): string => {
  if (!img) return "";
  return img.startsWith("/data/")
    ? `${process.env.REACT_APP_API}${img}`
    : img;
};

const formatDate = (date: string): string => {
  return dayjs(date)
    .add(543, "year")
    .locale("th")
    .format("DD MMMM YYYY");
};

const truncateTitle = (title: string, maxLength: number): string => {
  return title.length > maxLength 
    ? title.substring(0, maxLength) + "..." 
    : title;
};

// ✅ Sub Components
const NewsCard: React.FC<{ card: SuggestNews; onNavigate: (id: string) => void }> = 
  React.memo(({ card, onNavigate }) => (
    <div
      className="card-global"
      onClick={() => onNavigate(card.news_id)}
      style={{
        padding: "10px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        marginBottom: "16px",
        cursor: "pointer",
      }}
    >
      <Row align="middle">
        <Col xs={24} sm={24} md={24} lg={24} xl={8}>
          <img
            alt={card.title}
            src={getImageUrl(card.img)}
            className="card-image-detail"
            style={{ display: "block", margin: "0 auto" }}
            loading="lazy"
          />
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={16}>
          <div className="card-content">
            <Paragraph className="card-title">
              <strong>{card.title}</strong>
            </Paragraph>
            <span className="card-meta">
              <span>{formatDate(card.created_date)}</span>
            </span>
          </div>
        </Col>
      </Row>
    </div>
  ));

const DetailNewspage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const { maxLength, fontSize } = useResponsive();
  
  const [loading, setLoading] = useState(true);
  const [newsData, setNewsData] = useState<NewsData | null>(null);
  const [newsDetail, setNewsDetail] = useState<NewsDetail[]>([]);
  const [suggestNews, setSuggestNews] = useState<SuggestNews[]>([]);

  // ✅ Memoized Values
  const sanitizedDetails = useMemo(() => {
    return newsDetail.map((detail) => ({
      ...detail,
      text: DOMPurify.sanitize(detail.text),
    }));
  }, [newsDetail]);

  // ✅ Fetch Data
  const fetchNewsData = useCallback(async () => {
    if (!params.id) {
      message.error("Invalid news ID");
      navigate("/news");
      return;
    }

    setLoading(true);
    try {
      const [newsRes, suggestRes] = await Promise.all([
        get_news({ news_id: params.id }),
        get_suggest_news(),
      ]);

      // Process news data
      if (newsRes.result && newsRes.result.length > 0) {
        const news = newsRes.result[0];
        const details = typeof news.details === "string" 
          ? JSON.parse(news.details) 
          : news.details;
        
        setNewsData(news);
        setNewsDetail(details);
      } else {
        message.warning("News not found");
        navigate("/news");
      }

      // Process suggest news
      if (suggestRes.result) {
        setSuggestNews(suggestRes.result);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Unable to load news data");
      navigate("/news");
    } finally {
      setLoading(false);
    }
  }, [params.id, navigate]);

  useEffect(() => {
    fetchNewsData();
  }, [fetchNewsData]);

  // ✅ Navigate to another news (without reload)
  const handleNavigateToNews = useCallback((newsId: string) => {
    window.scrollTo(0, 0);
    navigate(`/news/detail/${newsId}`, { replace: true });
  }, [navigate]);

  // ✅ Loading State
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // ✅ Empty State
  if (!newsData) {
    return (
      <Layout>
        <Content className="detail-news-layout">
          <Empty description={t("No news data available")} />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content className="detail-news-layout">
        {/* Breadcrumb */}
        <div>
          <Row>
            <Col>
              <Breadcrumb
                separator=">"
                items={[
                  {
                    title: (
                      <a
                        style={{ fontWeight: "bold" }}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/news");
                        }}
                      >
                        {t("News")}
                      </a>
                    ),
                  },
                  {
                    title: (
                      <span style={{ fontWeight: "bold" }}>
                        {(newsData.title)}
                      </span>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
          <hr />
        </div>

        {/* Body News */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24} lg={15}>
            <Card style={{ marginTop: 16 }}>
              <Title level={3} style={{ padding: "20px", fontSize }}>
                {newsData.title}
              </Title>
              
              <div style={{ padding: "0px 20px 20px 20px" }}>
                <CalendarOutlined /> {" "}
                <Text style={{ fontSize: "16px" }} type="secondary">
                  {formatDate(newsData.created_date)}
                </Text>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 16,
                }}
              >
                <Image
                  src={getImageUrl(newsData.img)}
                  alt={newsData.title}
                  className="responsive-image"
                  placeholder={<Spin />}
                />
              </div>

              {sanitizedDetails.length > 0 ? (
                sanitizedDetails.map((detail) => (
                  <div
                    className="tiptap"
                    key={detail.id}
                    style={{ marginTop: 20, padding: "0px 20px 5px 20px" }}
                    dangerouslySetInnerHTML={{ __html: detail.text }}
                  />
                ))
              ) : (
                <Empty description={t("No content available")} />
              )}
            </Card>
          </Col>

          {/* Suggested News */}
          <Col xs={24} sm={24} md={24} lg={9}>
            <Divider
              orientation="left"
              style={{ fontSize: "25px", color: "#737373" }}
            >
              {t("Latest Update")}
            </Divider>
            
            {suggestNews.length > 0 ? (
              <div>
                {suggestNews.map((card) => (
                  <NewsCard 
                    key={card.news_id || card.id}  // ✅ ใช้ news_id เป็น key หลัก
                    card={card} 
                    onNavigate={handleNavigateToNews} 
                  />
                ))}
              </div>
            ) : (
              <Empty description={t("No suggested news")} />
            )}
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default DetailNewspage;